/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SliderWidget", ["FieldWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Slider widget.
   * @class SliderWidget
   * @memberOf classes
   * @extends classes.FieldWidgetBase
   * @publicdoc Widgets
   */
  cls.SliderWidget = context.oo.Class(cls.FieldWidgetBase, function ($super) {
    return /** @lends classes.SliderWidget.prototype */ {
      __name: "SliderWidget",
      /**
       * Redefine where the data is located
       * @type {string|Object}
       */
      __dataContentPlaceholderSelector: cls.WidgetBase.selfDataContent,
      /**
       * Slider orientation. Default is horizontal
       * @type {?string}
       */
      _orientation: null,

      /**
       * @constructs
       * @param {Object} opts - Options passed to the constructor
       * @publicdoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
        this.setVMFocusable(true);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this._ignoreLayout) {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutEngine = new cls.SliderLayoutEngine(this);
          this._layoutInformation.getSizePolicyConfig().initial = cls.SizePolicy.Dynamic();
          this._layoutInformation.getSizePolicyConfig().fixed = cls.SizePolicy.Dynamic();

          this._layoutInformation.getSizePolicyConfig().setAllShrinkable(false);
        }
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        cls.DOMElementHelper.on(this.getInputElement(), "touchend.SliderWidget", this._onSlide.bind(this));
        cls.DOMElementHelper.on(
          this.getInputElement(),
          "touchstart.SliderWidget",
          function () {
            this._preventContainerScrolling(true);
          }.bind(this)
        );
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        cls.DOMElementHelper.off(this.getInputElement(), "touchend.SliderWidget");
        cls.DOMElementHelper.off(this.getInputElement(), "touchstart.SliderWidget");
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageInput: function (dataString = "", event = null) {
        $super.manageInput.call(this, dataString, event);
        this.triggerValueChangedEvent(this.getValue(), true);
        return true;
      },

      /**
       * @inheritDoc
       * @param domEvent
       * @returns {boolean}
       */
      manageMouseDown: function (domEvent) {
        $super.manageMouseDown.call(this, domEvent);
        return true;
      },

      /**
       * @inheritDoc
       * @param domEvent
       * @returns {boolean}
       */
      manageMouseUp: function (domEvent) {
        $super.manageMouseUp.call(this, domEvent);

        this._onSlide.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onRequestFocus(domEvent); // request focus
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        if (this.isEnabled() && !this.isReadOnly()) {
          keyProcessed = true;
          switch (keyString) {
            case "down":
            case this.getStart():
              this.setEditing(true);
              const decVal = this._decrease();
              this.triggerValueChangedEvent(decVal.toString());
              break;
            case "up":
            case this.getEnd():
              this.setEditing(true);
              const incVal = this._increase();
              this.triggerValueChangedEvent(incVal.toString());
              break;
            case "home":
              this.setEditing(true);
              const incValMax = this._decreaseToMin();
              this.triggerValueChangedEvent(incValMax.toString());
              break;
            case "end":
              this.setEditing(true);
              const incValMin = this._increaseToMax();
              this.triggerValueChangedEvent(incValMin.toString());
              break;
            default:
              keyProcessed = false;
          }
        }

        if (keyProcessed) {
          return true;
        } else {
          return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
        }
      },

      /**
       * Manage key before any action
       * @param {string}  - key string representation
       * @param {Object} domKeyEvent - key event from DOM
       * @param {boolean} repeat - true if key is being pressed
       * @returns {boolean} returns if the domKeyEvent has been processed by the widget
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isInTable()) {
          return this.getTableWidgetBase().managePriorityKeyDown(keyString, domKeyEvent, repeat);
        } else if (this.isInScrollGrid()) {
          let scrollGridWidget = this.getScrollGridWidget() || this.getStretchableScrollGridWidgetBase();
          return scrollGridWidget.managePriorityKeyDown(keyString, domKeyEvent, repeat);
        }
        switch (keyString) {
          case "pagedown":
            this.setEditing(true);
            const decVal = this._decrease();
            this.triggerValueChangedEvent(decVal.toString());
            return true;
          case "pageup":
            this.setEditing(true);
            const incVal = this._increase();
            this.triggerValueChangedEvent(incVal.toString());
            return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },
      /**
       * Handler executed on mouse click. We update the value depending of pointer location click on the slider
       * and send it to VM
       * @param {UIEvent} evt - DOM event
       */
      _onSlide: function (evt) {
        if (!this.hasFocus()) {
          this._onRequestFocus(evt); // request focus
        }
        // need to emit change because on some browsers, change event is not raised when slider has not yet the focus
        if (evt.button === 0 && this.isEnabled() && !this.isReadOnly()) {
          let total;
          // Vertical sliders are rotated with CSS. evt.offsetX takes this into account. getBoundingClientRect doesn't
          let clickPos = 0;
          const inputRect = this._inputElement.getBoundingClientRect();
          if (evt.offsetX) {
            clickPos = evt.offsetX;
            // on mobile offsetX doesn't exist. We need to calculate the relative click position using global pageX and input position
          } else if (cls.WindowHelper.isTouchDevice() && evt.changedTouches[0]) {
            if (this.getOrientation() === "horizontal") {
              clickPos = evt.changedTouches[0].pageX - inputRect.left;
            } else {
              clickPos = inputRect.bottom - evt.changedTouches[0].pageY;
            }
          }
          if (this.getOrientation() === "horizontal") {
            total = inputRect.width;
          } else {
            total = inputRect.height;
          }
          const expectedTotal = this.getMax() - this.getMin();
          const expectedVal = expectedTotal * (clickPos / total);
          const step = this.getStep();
          let value = this.getMin() + Math.floor(expectedVal / step) * step;
          if (expectedVal % step > step / 2) {
            value += step;
          }
          value = cls.MathHelper.clamp(value, this.getMin(), this.getMax());
          this.setEditing(true);
          if (this.hasFocus()) {
            this._inputElement.value = value;
          }
          this.triggerValueChangedEvent(value);
        }
        this._preventContainerScrolling(false);
      },

      /**
       * Increase the displayed value
       */
      _increase: function () {
        const newValue = Math.min((this.getValue() || 0) + this.getStep(), this.getMax());
        if (this.hasFocus()) {
          this.setValue(newValue, false);
        }
        return newValue;
      },

      /**
       * Increase to the maximun the displayed value
       */
      _increaseToMax: function () {
        const newValue = this.getMax();
        if (this.hasFocus()) {
          this.setValue(newValue, false);
        }
        return newValue;
      },

      /**
       * Decrease the displayed value
       */
      _decrease: function () {
        const newValue = Math.max((this.getValue() || 0) - this.getStep(), this.getMin());
        if (this.hasFocus()) {
          this.setValue(newValue, false);
        }
        return newValue;
      },

      /**
       * Decrease to the minimum the displayed value
       */
      _decreaseToMin: function () {
        const newValue = this.getMin();
        if (this.hasFocus()) {
          this.setValue(newValue, false);
        }
        return newValue;
      },

      /**
       * @inheritDoc
       */
      getValue: function () {
        return Number.parseInt(this._inputElement.value, 10);
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM = false, cursorPosition = null) {
        $super.setValue.call(this, value, fromVM, cursorPosition);
        value = Number.parseInt(value, 10);
        this.getInputElement().value = cls.ObjectHelper.isNumber(value) && !Number.isNaN(value) ? value : 0;
        cls.DOMElementHelper.updateAriaAttribute(this.getElement(), "valuenow", value);
        const styleRule = { "--valueRatio": `${this._getValueRatio() * 100}%` };
        this.setStyle(styleRule);
      },

      /**
       * Get minimum possible value of the slider
       * @returns {number} the minimum value
       * @publicdoc
       */
      getMin: function () {
        return cls.DOMElementHelper.getIntAttribute(this._inputElement, "min");
      },

      /**
       * Define the minimum possible value of the slider
       * @param {number} value - the minimum value
       * @publicdoc
       */
      setMin: function (value) {
        if (cls.ObjectHelper.isNumber(value)) {
          this._inputElement.setAttribute("min", value);
        } else {
          this._inputElement.removeAttribute("min");
        }
        cls.DOMElementHelper.updateAriaAttribute(this.getElement(), "valuemin", value);
      },

      /**
       * Get maximum possible value of the slider
       * @returns {number} the maximum value
       * @publicdoc
       */
      getMax: function () {
        return cls.DOMElementHelper.getIntAttribute(this._inputElement, "max");
      },

      /**
       * Define the maximum possible value of the slider
       * @param {number} value - the maximum value
       * @publicdoc
       */
      setMax: function (value) {
        if (cls.ObjectHelper.isNumber(value)) {
          this._inputElement.setAttribute("max", value);
        } else {
          this._inputElement.removeAttribute("max");
        }
        cls.DOMElementHelper.updateAriaAttribute(this.getElement(), "valuemax", value);
      },

      /**
       * Get slider step when increasing or decreasing value
       * @returns {number} the step value
       * @publicdoc
       */
      getStep: function () {
        return cls.DOMElementHelper.getIntAttribute(this._inputElement, "step");
      },

      /**
       * Define the slider step when increasing or decreasing value
       * @param {number} step - the step value
       * @publicdoc
       */
      setStep: function (step) {
        this._inputElement.setAttribute("step", cls.ObjectHelper.isNumber(step) && step > 0 ? step : 1);
      },

      /**
       * Return the percentage of the value compared to the min and max
       * @returns {number} The ratio between 0 and 1.
       */
      _getValueRatio: function () {
        const ratio = (this.getValue() - this.getMin()) / (this.getMax() - this.getMin());
        return Number.isNaN(ratio) ? 0 : ratio;
      },

      /**
       * @inheritDoc
       */
      setTitle: function (title) {
        this._inputElement.setAttribute("title", title);
      },

      /**
       * @inheritDoc
       */
      getTitle: function () {
        return this._inputElement.getAttribute("title");
      },

      /**
       * Set the orientation of the slider widget
       * @param {string} orientation can be 'horizontal' or 'vertical'
       * @publicdoc
       */
      setOrientation: function (orientation) {
        if (this._orientation === orientation) {
          return;
        }

        this._orientation = orientation;
        this.toggleClasses(orientation === "vertical", "vertical");
        cls.DOMElementHelper.updateAriaAttribute(this.getElement(), "orientation", orientation);

        if (this.getLayoutEngine()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * Get the current slider orientation. Default is horizontal.
       * @returns {string} current css orientation
       * @publicdoc
       */
      getOrientation: function () {
        return this._orientation ? this._orientation : "horizontal";
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        this.domFocus(preventScroll);
        $super.setFocus.call(this, fromMouse, preventScroll);
      },

      /**
       * @inheritDoc
       */
      setReadOnly: function (readonly) {
        $super.setReadOnly.call(this, readonly);
        this._setInputReadOnly(readonly);
      },

      /**
       * Set if input element is readonly
       * @param {boolean} readonly
       * @private
       */
      _setInputReadOnly: function (readonly) {
        if (readonly) {
          this._inputElement.setAttribute("readonly", "readonly");
        } else {
          this._inputElement.removeAttribute("readonly");
        }
      },

      /**
       * Prevent the container to scroll while doing touch to scroll the tab-titles
       * @param {Boolean} prevent - true to prevent it, false otherwise
       * @private
       */
      _preventContainerScrolling: function (prevent) {
        const form = this.getFormWidget();
        if (form) {
          cls.DOMElementHelper.toggleClass(form.getContainerElement(), "prevent-touch-scroll", prevent);
        }
      },

      /**
       * @inheritDoc
       */
      getValueForClipboard: function (ignoreSelection = false) {
        return this.getValue();
      },

      /**
       * @inheritDoc
       */
      getContextMenuAuthorizedActions: function () {
        return {
          paste: false,
          copy: true,
          cut: false,
          selectAll: false
        };
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Slider", cls.SliderWidget);
});
